import { useMutation, useQueries, useQueryClient, type QueryKey } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { memo, type ReactElement, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { getMetrics } from '@/features/metrics/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  useProjectFilter,
  useProjectMetricThresholds,
  useProjectsQuery,
} from '@/features/projects';
import { CLONE_TABLE_ROW_HEIGHT } from '@/features/shared/components/cloneTable/cloneTableConstants';
import { cloneTableSx } from '@/features/shared/components/cloneTable/cloneTableSx';
import { resolveEntityTags } from '@/features/shared/tags';
import { DeleteTestDialog } from '@/features/tests/components/dialogs/TestDialogs';
import { testsGridConfig } from '@/features/tests/grid/gridConfig';
import { useDeleteTest, useUpdateTest } from '@/features/tests/hooks/tests';
import { useNotifications } from '@/providers/notificationHooks';
import { useApiErrorMessage } from '@/shared/data/errors';
import { updateTagColor, type TagColor } from '@/shared/data/tags';
import { queryKeys, useTagOptions } from '@/shared/hooks';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { type DataGridProps, type GridFilterModel, type GridSortModel } from '@/ui/primitives';
import { buildTestsColumns } from './testsColumns';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  MetricThresholdGroup,
  QueryResult,
  TestExecutionStatus,
  TestListItemDto,
} from '@flowqa/shared';

type TestsTableCloneProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  disableColumnSorting?: boolean;
  disableColumnMenu?: boolean;
  showToolbar?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<TestListItemDto>>;
  queryKey?: QueryKey;
  onRowsChange?: (rows: TestListItemDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  showSuiteLinks?: boolean;
  includeSuiteInNameColumn?: boolean;
  onStatusSelect?: (status: TestExecutionStatus | null) => void;
  showDeltas?: boolean;
  sparklineRangeDays?: number;
  tokenOverride?: string | null;
  disableLinks?: boolean;
  search?: string;
  metricsStartDay?: string;
  metricsEndDay?: string;
  columnVisibilityModel?: Record<string, boolean>;
  onColumnVisibilityModelChange?: DataGridProps['onColumnVisibilityModelChange'];
};

const TestsTableCloneComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting = false,
  disableColumnMenu = false,
  showToolbar = true,
  noRowsText = 'No tests found',
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  onRowsChange,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  showSuiteLinks = true,
  includeSuiteInNameColumn = true,
  onStatusSelect,
  showDeltas = true,
  sparklineRangeDays = 7,
  tokenOverride,
  disableLinks = false,
  search,
  metricsStartDay,
  metricsEndDay,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
}: TestsTableCloneProps): ReactElement => {
  const { session } = useAuth();
  const resolvedToken = tokenOverride ?? session?.tokens.accessToken ?? null;
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const queryClient = useQueryClient();
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const deleteTestMutation = useDeleteTest();
  const updateTestMutation = useUpdateTest();
  const { tagOptions } = useTagOptions({ projectId: selectedProjectId });
  const [updatingTagRowId, setUpdatingTagRowId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
    projectId: string;
  } | null>(null);
  const [visibleRows, setVisibleRows] = useState<TestListItemDto[]>([]);
  const tagColorByName = useMemo(
    () =>
      tagOptions.reduce<Record<string, TagColor>>((acc, option) => {
        acc[option.value] = option.color;
        acc[option.value.trim().toLowerCase()] = option.color;
        return acc;
      }, {}),
    [tagOptions],
  );
  const updateTagColorMutation = useMutation({
    mutationFn: async (input: { tags: string[]; colors: TagColor[] }) => {
      const updates = input.tags
        .map((name, index) => ({ name: name.trim(), color: input.colors[index] ?? 'neutral' }))
        .filter((item) => item.name.length > 0);
      if (updates.length === 0) return;
      await Promise.all(
        updates.map(async (item) => {
          await updateTagColor(
            item,
            resolvedToken ?? undefined,
            selectedOrganisationId ?? undefined,
          );
        }),
      );
    },
  });
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const projectsQuery = useProjectsQuery();
  const thresholdsByProjectId = useMemo(() => {
    const entries = projectsQuery.data?.items ?? [];
    return new Map(entries.map((project) => [project.id, project.metricThresholds.tests]));
  }, [projectsQuery.data?.items]);
  const resolveThresholds = useMemo(
    () =>
      (row: { projectId: string }): MetricThresholdGroup | undefined =>
        thresholdsByProjectId.get(row.projectId),
    [thresholdsByProjectId],
  );
  const metricsRange = sparklineRangeDays === 14 ? '14d' : sparklineRangeDays === 30 ? '30d' : '7d';
  const metricsRangeKey =
    metricsStartDay && metricsEndDay ? `${metricsStartDay}_${metricsEndDay}` : 'all';
  const perRowMetricsQueries = useQueries({
    queries: visibleRows.map((row) => ({
      queryKey: queryKeys.metrics(
        selectedOrganisationId ?? null,
        row.projectId,
        'test',
        metricsRange,
        'all',
        'all',
        row.id,
        'all',
        metricsRangeKey,
      ),
      enabled: Boolean(resolvedToken),
      staleTime: 30_000,
      queryFn: async () =>
        getMetrics(
          {
            range: metricsRange,
            scope: 'test',
            statuses: [],
            projectId: row.projectId,
            entityId: row.id,
            startDay: metricsStartDay,
            endDay: metricsEndDay,
          },
          selectedOrganisationId ?? undefined,
          resolvedToken,
        ),
    })),
  });
  const metricSeriesByTestId = useMemo(() => {
    const result = new Map<
      string,
      {
        dates: Date[];
        passRate: number[];
        frequencyPerDay: number[];
        averageDurationMs: number[];
      }
    >();
    visibleRows.forEach((row, index) => {
      const queryData = perRowMetricsQueries[index]?.data;
      if (!queryData) return;
      const dates = queryData.metricsRangeDates.map((day) => dayjs(day).toDate());
      const passRate = queryData.series.filteredPassRate.map((point) => point.value);
      const frequencyPerDay = queryData.series.executionTrendMetrics.map((point) => point.value);
      const averageDurationMs = queryData.series.executionDurationAverage.map(
        (point) => point.value * 1000,
      );
      if (
        dates.length === 0 ||
        passRate.length !== dates.length ||
        frequencyPerDay.length !== dates.length ||
        averageDurationMs.length !== dates.length
      ) {
        return;
      }
      result.set(row.id, { dates, passRate, frequencyPerDay, averageDurationMs });
    });
    return result;
  }, [perRowMetricsQueries, visibleRows]);
  const shouldQueryPerRowMetrics = resolvedToken !== null;
  const metricsQueriesReady =
    !shouldQueryPerRowMetrics ||
    visibleRows.length === 0 ||
    perRowMetricsQueries.every(
      (query) => query.data !== undefined || (!query.isPending && !query.isFetching),
    );
  const filterSuites = useMemo(() => {
    const options = new Map<string, string>();
    visibleRows.forEach((row) => {
      const suiteItems = row.suites?.items ?? [];
      suiteItems.forEach((suite) => {
        if (!options.has(suite.id)) {
          options.set(suite.id, suite.name);
        }
      });
      const suiteIds = row.suites?.ids ?? [];
      suiteIds.forEach((suiteId) => {
        if (!options.has(suiteId)) {
          options.set(suiteId, suiteId);
        }
      });
    });
    return Array.from(options.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [visibleRows]);
  const filterTags = useMemo(() => {
    const tags = new Set<string>();
    visibleRows.forEach((row) => {
      resolveEntityTags(row).forEach((tag) => {
        tags.add(tag);
      });
    });
    return Array.from(tags.values()).sort((left, right) => left.localeCompare(right));
  }, [visibleRows]);
  const defaultColumns = useMemo(
    () =>
      buildTestsColumns({
        selectedProjectId,
        onProjectSelect: setSelectedProjectId,
        onStatusSelect,
        thresholds: thresholds.tests,
        resolveThresholds,
        showSuiteLinks: showSuiteLinks && !disableLinks,
        combineNameAndSuite: true,
        includeSuiteInNameColumn,
        includeTagsInNameColumn: true,
        includeTagsColumn: false,
        includeActionsColumn: true,
        disableColumnResizeHandles: false,
        sparklineRangeDays,
        passRateAsSparklineCell: true,
        frequencyAsSparklineCell: true,
        avgDurationAsSparklineCell: true,
        latestAsMeta: true,
        includeLastExecutedColumn: false,
        disableLinks,
        showDeltas,
        metricSeriesByTestId,
        filterSuites,
        filterTags,
        onDeleteRow: (row) => {
          setDeleteTarget({ id: row.id, name: row.name, projectId: row.projectId });
        },
        deleteRowPendingId: deleteTestMutation.isPending ? (deleteTarget?.id ?? null) : null,
        tagColorByName,
        onUpdateRowTagsColor: ({ row, tags, colors }) => {
          const normalizedTags = Array.from(
            new Set(tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0)),
          );
          const normalizedColors = colors.slice(0, normalizedTags.length);
          setUpdatingTagRowId(row.id);
          void (async () => {
            try {
              await updateTestMutation.mutateAsync({ id: row.id, tags: normalizedTags });
              if (normalizedTags.length > 0) {
                await updateTagColorMutation.mutateAsync({
                  tags: normalizedTags,
                  colors: normalizedColors,
                });
              }
              notify({ message: 'Tags updated.', tone: 'green' });
              await queryClient.invalidateQueries({
                queryKey: queryKeys.tagOptions(selectedProjectId, selectedOrganisationId ?? null),
              });
            } catch (error) {
              notify({ message: toErrorMessage(error), tone: 'red' });
            } finally {
              setUpdatingTagRowId(null);
            }
          })();
        },
        updateRowTagsColorPendingId:
          updateTagColorMutation.isPending || updateTestMutation.isPending
            ? updatingTagRowId
            : null,
      }),
    [
      tagColorByName,
      updateTagColorMutation,
      queryClient,
      disableLinks,
      onStatusSelect,
      resolveThresholds,
      selectedProjectId,
      selectedOrganisationId,
      setSelectedProjectId,
      showSuiteLinks,
      includeSuiteInNameColumn,
      showDeltas,
      sparklineRangeDays,
      thresholds.tests,
      metricSeriesByTestId,
      filterSuites,
      filterTags,
      deleteTarget?.id,
      deleteTestMutation.isPending,
      updateTestMutation,
      updatingTagRowId,
      notify,
      toErrorMessage,
    ],
  );
  const togglableColumnFields = useMemo(() => {
    const helperOnlyFields = new Set([
      'suiteId',
      'tags',
      'statusFilter',
      'latestExecutionDurationMs',
      'latestExecutionTimestamp',
    ]);
    return defaultColumns
      .map((column) => column.field)
      .filter((field) => !helperOnlyFields.has(field));
  }, [defaultColumns]);

  const gridConfig = testsGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey === undefined ? 'tests-table' : persistKey,
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    includeDeltas: showDeltas,
    include: showSuiteLinks
      ? undefined
      : {
          latestExecution: true,
          metrics: true,
          metricDeltas: showDeltas,
        },
  });

  return (
    <>
      <ServerDataTable<TestListItemDto>
        queryPath="tests/query"
        {...gridConfig}
        queryFn={queryFn ?? gridConfig.queryFn}
        queryKey={queryKey ?? gridConfig.queryKey}
        columns={defaultColumns}
        getRowId={(row) => row.id}
        token={resolvedToken}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        pageSizeOptions={pageSizeOptions}
        initialPageSize={initialPageSize}
        search={search}
        columnVisibilityModel={columnVisibilityModel}
        disablePageSizeSelect={disablePageSizeSelect}
        hideFooter={hideFooter}
        disableColumnMenu={disableColumnMenu}
        disableColumnSorting={disableColumnSorting}
        showToolbar={showToolbar}
        showColumnVerticalBorder={false}
        showCellVerticalBorder={false}
        noRowsText={noRowsText}
        disableCellFocus
        getRowHeight={() => CLONE_TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={() => CLONE_TABLE_ROW_HEIGHT}
        showSkeletonOnFetch
        showLastUpdated={showLastUpdated}
        autoHeight
        externalLoading={!metricsQueriesReady}
        onRowsChange={(rows) => {
          setVisibleRows(rows);
          onRowsChange?.(rows);
        }}
        onQueryInput={onQueryInput}
        onDataUpdatedAt={onDataUpdatedAt}
        gridProps={{
          onColumnVisibilityModelChange,
          slotProps: {
            toolbar: {
              showQuickFilter: true,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
        sx={cloneTableSx}
      />
      <DeleteTestDialog
        open={deleteTarget !== null}
        testName={deleteTarget?.name}
        loading={deleteTestMutation.isPending}
        onClose={() => {
          if (deleteTestMutation.isPending) return;
          setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (!deleteTarget) return;
          const deletedTestName = deleteTarget.name;
          deleteTestMutation.mutate(
            { id: deleteTarget.id, projectId: deleteTarget.projectId },
            {
              onSuccess: () => {
                notify({ message: `Test '${deletedTestName}' deleted.`, tone: 'green' });
                setDeleteTarget(null);
              },
              onError: (error) => {
                notify({ message: toErrorMessage(error), tone: 'red' });
              },
            },
          );
        }}
      />
    </>
  );
};

export const TestsTableClone = memo(TestsTableCloneComponent);
